/**
 * Manual deal entry — create path using the same field catalogue as Deal Dossier.
 *
 * Renders Chris's intake layout (~30 fields), not every catalogue row.
 * Create opens a Review & Confirm step; Confirm & Submit posts POST /loans
 * and records acceptance on the existing confirmation endpoint.
 */

import { useId, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  ExternalLink,
  Loader2,
  StickyNote,
} from 'lucide-react'
import { deriveCatalogue } from '@/components/deal/dossier/deriveCatalogue'
import { formatMoney, formatPoints } from '@/components/deal/dossier/format'
import {
  buildCreatePayload,
  dealFileService,
  fieldValueError,
  parseMoneyText,
  type DealFieldDef,
} from '@/services/dealFile'
import { dealConfirmation } from '@/services/dealConfirmation'
import { loansService, type PossibleDuplicateDeal } from '@/services/loans'
import { mapsConfigService } from '@/services/mapsConfig'
import { referenceDataService } from '@/services/referenceData'
import { referenceCategoryFor } from '@/components/deal/dossier/editableFields'
import { AddressAutocomplete, type ResolvedAddress } from '@/components/address/AddressAutocomplete'
import { LOAN_QUERY_KEYS } from '@/services/loanQueryKeys'
import { useDealDossierStore } from '@/store/dealDossierStore'
import { useLocalAuth } from '@/auth/LocalAuthContext'
import { formatDeskDate } from '@/utils/dates'
import BrokerSelect, { type BrokerChoice } from './BrokerSelect'
import ManualEntryReview from './ManualEntryReview'
import { assumedValue, emptyOptionLabel } from './intakeDefaults'
import {
  BACKSHOP_HINT_FIELDS,
  CALCULATED_READONLY_FIELDS,
  MANUAL_ENTRY_DEFAULT_OPEN,
  MANUAL_ENTRY_SECTION_LABELS,
  MANUAL_ENTRY_SECTION_ORDER,
  fieldsInSection,
  hideOnForm,
  intakeStateOptions,
  isPairLeft,
  pairMate,
  placementOf,
  sectionDomId,
  type ManualEntrySectionId,
  withManualEntryLabel,
} from './manualEntryLayout'
import { buildReviewModel, keyedFieldNames } from './manualEntryReviewModel'
import { measureDisplayLabel } from './measureDefaults'
import {
  blocksCreate,
  canPlacePastTriage,
  formatCurrencyTyping,
  freshEntryValues,
  intakeRequiredErrors,
  missingBackshopRequired,
  offeredTotal,
  requiredFieldErrorsFromRefusal,
  stageChoices,
} from './manualEntryRules'

function displayValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  return String(value)
}

function sectionLabelFor(name: string): string | undefined {
  const place = placementOf(name)
  if (!place || place === 'profile') return undefined
  return MANUAL_ENTRY_SECTION_LABELS[place]
}

export default function ManualDealEntry() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const openDossier = useDealDossierStore((s) => s.openDossier)
  const { localUser } = useLocalAuth()
  const isAdmin = canPlacePastTriage(localUser)

  const catalogue = useQuery({
    queryKey: LOAN_QUERY_KEYS.dealFieldCatalogue,
    queryFn: () => dealFileService.getCatalogue(),
    staleTime: 5 * 60 * 1000,
  })

  const mapsConfig = useQuery({
    queryKey: ['maps-config'],
    queryFn: () => mapsConfigService.get(),
    staleTime: 10 * 60 * 1000,
  })

  const fields = useMemo(() => (catalogue.data?.fields ?? []).map(withManualEntryLabel), [catalogue.data?.fields])

  const [values, setValues] = useState<Record<string, string>>(() => freshEntryValues())
  const [owned, setOwned] = useState<Record<string, boolean>>({})
  const [brokerId, setBrokerId] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [open, setOpen] = useState<Record<string, boolean>>(MANUAL_ENTRY_DEFAULT_OPEN)
  const [openingNote, setOpeningNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [step, setStep] = useState<'edit' | 'review'>('edit')
  const [created, setCreated] = useState<{
    id: string
    property: string
    stage: string
    stillBlank: string[]
    keyed: string[]
    acceptedBy?: string
    acceptedAt?: string
    acceptanceError?: string
  } | null>(null)
  const [apiError, setApiError] = useState<string | null>(null)
  const [duplicates, setDuplicates] = useState<PossibleDuplicateDeal[] | null>(null)

  const derived = useMemo(() => deriveCatalogue(values, Object.keys(owned).filter((k) => owned[k])), [values, owned])

  const reviewModel = useMemo(
    () => buildReviewModel(fields, values, owned, isAdmin),
    [fields, values, owned, isAdmin],
  )

  const set = (key: string, value: string) => {
    setOwned((prev) => (prev[key] ? prev : { ...prev, [key]: true }))
    setValues((prev) => {
      const next = { ...prev, [key]: value }
      if ((key === 'loan_amount' || key === 'future_funding') && !owned.fully_funded_amount) {
        const total = offeredTotal(next)
        next.fully_funded_amount = total ? formatCurrencyTyping(total) : ''
      }
      if (key === 'underwriter' && !owned.assignee && (!prev.assignee || prev.assignee === 'Unassigned')) {
        next.assignee = value || 'Unassigned'
      }
      if ((key === 'units' || key === 'unit_of_measure') && !owned.sf) {
        const measure = (key === 'unit_of_measure' ? value : next.unit_of_measure || '').trim().toLowerCase()
        const units = key === 'units' ? value : next.units
        if (measure === 'sf' && (units || '').trim()) next.sf = units
      }
      return next
    })
    setErrors((prev) => ({
      ...prev,
      [key]: '',
      ...(key === 'loan_amount' || key === 'future_funding' ? { fully_funded_amount: '' } : {}),
    }))
  }

  const setBroker = (choice: BrokerChoice) => {
    setBrokerId(choice.brokerId)
    setOwned((prev) => ({
      ...prev,
      ...(choice.firm ? { brokerage_company: true } : {}),
      ...(choice.contact ? { broker_name: true } : {}),
    }))
    setValues((prev) => ({ ...prev, brokerage_company: choice.firm, broker_name: choice.contact }))
  }

  const setAddress = (address: ResolvedAddress) => {
    setValues((prev) => {
      const next = { ...prev }
      const fill = (key: string, value: string) => {
        if (value && !(next[key] || '').trim()) next[key] = value
      }
      if (address.street) next.street = address.street
      fill('city', address.city)
      fill('state', address.state)
      fill('zip', address.postalCode)
      fill('country', address.country)
      const cityState = [address.city, address.state].filter(Boolean).join(', ')
      fill('location', cityState)
      return next
    })
    setErrors((prev) => ({ ...prev, street: '', city: '', state: '', zip: '' }))
  }

  const filledCount = useMemo(
    () =>
      MANUAL_ENTRY_SECTION_ORDER.flatMap((id) => fieldsInSection(fields, id)).filter(
        (f) => !hideOnForm(f.name, values, isAdmin) && (values[f.name] || '').trim(),
      ).length,
    [fields, values, isAdmin],
  )

  const expandSections = (bad: Set<string>) => {
    setOpen((prev) => {
      const nextOpen = { ...prev }
      for (const name of bad) {
        const label = sectionLabelFor(name)
        if (label) nextOpen[label] = true
      }
      return nextOpen
    })
  }

  const validate = (): boolean => {
    const next: Record<string, string> = {}
    const intake = intakeRequiredErrors(values)
    for (const f of fields) {
      const raw = (values[f.name] || '').trim()
      if (f.name in intake) next[f.name] = intake[f.name]
      else if (blocksCreate(f) && !raw && f.name !== 'fully_funded_amount') next[f.name] = 'Required'
      if (!next[f.name] && raw && (f.kind === 'currency' || f.kind === 'percent' || f.kind === 'number')) {
        const numericOk =
          f.kind === 'currency'
            ? parseMoneyText(raw) !== null
            : Number.isFinite(Number(raw.replace(/[$,%\s,]/g, '').replace(/x$/i, '')))
        if (!numericOk) next[f.name] = 'Enter a number'
        else {
          const message = fieldValueError(f, raw)
          if (message) next[f.name] = message
        }
      }
    }
    if (next.location && ((values.city || '').trim() || (values.state || '').trim())) {
      delete next.location
    }
    setErrors(next)
    if (Object.keys(next).length) {
      expandSections(new Set(Object.keys(next)))
      setStep('edit')
    }
    return Object.keys(next).length === 0
  }

  const recordAcceptance = async (
    loanId: string,
    keyed: string[],
  ): Promise<
    | { ok: true; acceptedBy: string; acceptedAt: string }
    | { ok: false; message: string }
  > => {
    try {
      await dealConfirmation.record(
        loanId,
        keyed.map((field) => ({ field, action: 'confirm' })),
        'Accepted on manual entry review',
      )
      return {
        ok: true,
        acceptedBy: localUser?.name || localUser?.email || 'desk user',
        acceptedAt: new Date().toISOString(),
      }
    } catch {
      return {
        ok: false,
        message: 'The deal was saved, but review acceptance was not recorded. Retry without creating it again.',
      }
    }
  }

  const retryAcceptance = async () => {
    if (!created?.keyed.length || created.acceptedBy) return
    if (saving) return
    setSaving(true)
    const acceptance = await recordAcceptance(created.id, created.keyed)
    setCreated({
      ...created,
      acceptedBy: acceptance.ok ? acceptance.acceptedBy : undefined,
      acceptedAt: acceptance.ok ? acceptance.acceptedAt : undefined,
      acceptanceError: acceptance.ok ? undefined : acceptance.message,
    })
    setSaving(false)
  }

  const goToReview = () => {
    if (!validate()) return
    setApiError(null)
    setDuplicates(null)
    setStep('review')
  }

  const returnToForm = (sectionLabel?: string) => {
    setStep('edit')
    if (sectionLabel) {
      setOpen((prev) => ({ ...prev, [sectionLabel]: true }))
      requestAnimationFrame(() => {
        document.getElementById(sectionDomId(sectionLabel))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    }
  }

  const submit = async (confirmedNotDuplicate = false) => {
    if (!validate()) return
    if (saving) return
    setSaving(true)
    setApiError(null)
    try {
      if (!confirmedNotDuplicate) {
        const check = await loansService
          .possibleDuplicates({
            property_name: (values.property_name || '').trim() || undefined,
            street: (values.street || '').trim() || undefined,
            city: (values.city || '').trim() || undefined,
            state: (values.state || '').trim() || undefined,
            location: (values.location || '').trim() || undefined,
            sponsor: (values.sponsor || '').trim() || undefined,
          })
          .catch(() => null)
        if (check && check.candidates.length > 0) {
          setDuplicates(check.candidates)
          setStep('review')
          return
        }
      }
      setDuplicates(null)
      const payloadValues = { ...values }
      delete payloadValues.initial_funding
      for (const name of CALCULATED_READONLY_FIELDS) delete payloadValues[name]
      for (const name of Object.keys(payloadValues)) {
        // Stage is hidden from non-admins but still submitted as the Triage default.
        if (name !== 'stage' && hideOnForm(name, values, isAdmin)) delete payloadValues[name]
      }
      const payload = buildCreatePayload(fields, payloadValues)
      payload.entry_mode = 'manual'
      if (brokerId) payload.broker_id = brokerId
      const loan = await loansService.create(payload as never)

      if (openingNote.trim()) {
        try {
          await loansService.addNote(loan.id, openingNote.trim())
        } catch {
          /* deal is saved; note failure must not read as a failed create */
        }
      }

      const keyed = keyedFieldNames(values, owned, isAdmin)
      const acceptance = keyed.length ? await recordAcceptance(loan.id, keyed) : null

      queryClient.invalidateQueries({ queryKey: ['loans'] })
      setCreated({
        id: loan.id,
        property: String(payload.property_name ?? values.property_name ?? loan.id),
        stage: String(loan.stage ?? values.stage ?? 'Triage'),
        stillBlank: missingBackshopRequired(fields, values),
        keyed,
        acceptedBy: acceptance?.ok ? acceptance.acceptedBy : undefined,
        acceptedAt: acceptance?.ok ? acceptance.acceptedAt : undefined,
        acceptanceError: acceptance && !acceptance.ok ? acceptance.message : undefined,
      })
      setStep('edit')
      openDossier(loan.id, 'overview', loan)
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      const required = requiredFieldErrorsFromRefusal(detail)
      if (required) {
        setErrors(required)
        expandSections(new Set(Object.keys(required)))
        setStep('edit')
      }
      setApiError(
        required
          ? (detail as { message?: string }).message ?? 'Required fields are missing.'
          : typeof detail === 'string'
            ? detail
            : 'Could not save the deal. Check required fields and the API.',
      )
    } finally {
      setSaving(false)
    }
  }

  const resetForm = () => {
    setValues(freshEntryValues())
    setOwned({})
    setBrokerId('')
    setErrors({})
    setOpeningNote('')
    setCreated(null)
    setApiError(null)
    setStep('edit')
    setDuplicates(null)
  }

  if (created) {
    return (
      <div className="panel" data-testid="manual-entry-success">
        <div className="panel-body flex flex-col items-center py-8 gap-3">
          <CheckCircle2 size={28} className="text-emerald-400" />
          <p className="text-[13px] text-stone-200 font-medium">
            {created.property} logged as {created.id}
          </p>
          <p className="text-[11px] text-stone-500">
            The deal dossier is open. The deal is in the pipeline at {created.stage}.
          </p>
          {created.acceptedBy && created.acceptedAt ? (
            <p className="text-[11px] text-emerald-200/90" data-testid="manual-entry-accepted">
              Accepted by {created.acceptedBy} on {formatDeskDate(created.acceptedAt)}
            </p>
          ) : null}
          {created.acceptanceError ? (
            <div className="max-w-lg space-y-2 text-center" data-testid="manual-entry-acceptance-error">
              <p className="text-[11px] text-amber-200/90">{created.acceptanceError}</p>
              <button
                type="button"
                onClick={() => void retryAcceptance()}
                disabled={saving}
                className="btn-secondary text-[11px] px-3 py-1.5 disabled:opacity-50"
                data-testid="manual-entry-acceptance-retry"
              >
                {saving ? 'Retrying…' : 'Retry acceptance'}
              </button>
            </div>
          ) : null}
          {created.stillBlank.length ? (
            <p className="max-w-lg text-center text-[11px] text-amber-200/80">
              Still blank (required in Backshop): {created.stillBlank.join(', ')}. They show as missing on the
              dossier until filled — nothing was invented for them.
            </p>
          ) : null}
          <div className="flex gap-2 mt-1">
            <button
              onClick={() => openDossier(created.id)}
              className="btn-primary flex items-center gap-1.5 text-[11px] px-4 py-1.5"
            >
              <ArrowRight size={12} /> Open dossier
            </button>
            <button
              onClick={() => navigate('/pipeline')}
              className="btn-secondary flex items-center gap-1.5 text-[11px] px-3 py-1.5"
            >
              View in Pipeline
            </button>
            <button onClick={resetForm} className="btn-secondary text-[11px] px-3 py-1.5">
              Enter another deal
            </button>
          </div>
        </div>
      </div>
    )
  }

  const inputCls = (key: string) =>
    `w-full bg-black/30 border rounded-lg px-2.5 py-1.5 text-[11px] text-stone-200 ` +
    `[color-scheme:dark] placeholder-stone-600 focus:outline-none transition-colors ${
      errors[key] ? 'border-red-500/50 focus:border-red-400' : 'border-amber-400/20 focus:border-amber-400/40'
    }`

  if (catalogue.isLoading) {
    return (
      <div className="panel">
        <div className="panel-body flex items-center justify-center gap-2 py-12 text-[12px] text-stone-500">
          <Loader2 size={14} className="animate-spin" /> Loading field catalog…
        </div>
      </div>
    )
  }

  if (catalogue.isError) {
    return (
      <div className="panel">
        <div className="panel-body py-8 text-center text-[12px] text-red-300">
          Could not load the deal field catalog.
        </div>
      </div>
    )
  }

  if (step === 'review') {
    return (
      <ManualEntryReview
        dealName={(values.property_name || '').trim()}
        model={reviewModel}
        openingNote={openingNote}
        duplicates={duplicates}
        saving={saving}
        apiError={apiError}
        onEdit={() => returnToForm()}
        onEditSection={(label) => returnToForm(label)}
        onConfirm={() => submit()}
        onCreateAnyway={() => submit(true)}
      />
    )
  }

  const renderField = (f: DealFieldDef, visibleFields: DealFieldDef[]) => {
    if (f.name === 'brokerage_company' || f.name === 'broker_name') {
      const anchor = visibleFields.some((x) => x.name === 'brokerage_company')
        ? 'brokerage_company'
        : 'broker_name'
      if (f.name !== anchor) return null
      return (
        <BrokerSelect
          key="broker-select"
          choice={{
            firm: values.brokerage_company ?? '',
            contact: values.broker_name ?? '',
            brokerId,
          }}
          onChange={setBroker}
          className={inputCls(f.name)}
        />
      )
    }
    return (
      <FieldInput
        key={f.name}
        def={f}
        value={values[f.name] ?? ''}
        error={errors[f.name]}
        className={inputCls(f.name)}
        onChange={(v) => set(f.name, v)}
        onResolved={setAddress}
        mapsKey={mapsConfig.data?.apiKey ?? null}
        isAdmin={isAdmin}
        assetType={values.asset_type}
        currency={values.currency}
        derivedValue={derived.derived[f.name]}
        derivedNote={derived.notes[f.name]}
      />
    )
  }

  return (
    <div className="panel" data-testid="manual-entry-form">
      <div className="panel-header flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList size={14} className="text-amber-400" />
          <span className="text-[12px] font-medium text-stone-200">Manual Deal Entry</span>
        </div>
        <span className="text-[10px] text-stone-600">{filledCount} fields filled</span>
      </div>
      <div className="panel-body space-y-3">
        {MANUAL_ENTRY_SECTION_ORDER.map((id) => (
          <FormSection
            key={id}
            id={id}
            fields={fields}
            values={values}
            isAdmin={isAdmin}
            open={open[MANUAL_ENTRY_SECTION_LABELS[id]] ?? false}
            onToggle={() =>
              setOpen((p) => {
                const label = MANUAL_ENTRY_SECTION_LABELS[id]
                return { ...p, [label]: !p[label] }
              })
            }
            renderField={renderField}
          />
        ))}

        <section className="rounded-lg border border-amber-400/10 p-3 space-y-2">
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-stone-300">
            <StickyNote size={12} /> Opening note
          </div>
          <textarea
            value={openingNote}
            onChange={(e) => setOpeningNote(e.target.value)}
            rows={3}
            placeholder="Optional note stamped on the deal file…"
            className={inputCls('note')}
          />
        </section>

        {apiError && <p className="text-[11px] text-red-300">{apiError}</p>}

        {duplicates && duplicates.length > 0 && (
          <div
            role="alert"
            className="space-y-2 rounded-lg border border-amber-400/30 bg-amber-400/[0.06] p-3"
            data-testid="manual-entry-duplicates"
          >
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-amber-200">
              <AlertTriangle size={12} />
              This deal may already be on the pipeline
            </div>
            <p className="text-[10px] text-stone-400">
              Creating it again counts it twice against Backshop. Open the existing deal to check,
              or create a new one if it really is a different deal.
            </p>
            {duplicates.map((d) => (
              <div key={d.loanId} className="flex items-center gap-2 rounded border border-stone-700 bg-black/30 px-2.5 py-1.5">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[11px] font-medium text-stone-200">{d.property || d.loanId}</div>
                  <div className="truncate text-[10px] text-stone-500">
                    {[d.location, d.loanAmount ? `$${(d.loanAmount / 1_000_000).toLocaleString('en-US', { maximumFractionDigits: 2 })}M` : null, d.reason]
                      .filter(Boolean)
                      .join(' · ')}
                    <span className="font-mono"> · {d.loanId}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => openDossier(d.loanId)}
                  className="flex shrink-0 items-center gap-1 rounded border border-amber-400/30 px-2 py-1 text-[10px] text-amber-300 hover:bg-amber-400/10"
                >
                  <ExternalLink size={10} /> Open deal
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={goToReview}
            disabled={saving}
            className="btn-primary flex items-center gap-1.5 text-[11px] px-4 py-1.5 disabled:opacity-50"
          >
            Create deal
          </button>
        </div>
      </div>
    </div>
  )
}

function FormSection({
  id,
  fields,
  values,
  isAdmin,
  open,
  onToggle,
  renderField,
}: {
  id: ManualEntrySectionId
  fields: DealFieldDef[]
  values: Record<string, string>
  isAdmin: boolean
  open: boolean
  onToggle: () => void
  renderField: (f: DealFieldDef, visible: DealFieldDef[]) => ReactNode
}) {
  const label = MANUAL_ENTRY_SECTION_LABELS[id]
  const sectionFields = fieldsInSection(fields, id).filter((f) => !hideOnForm(f.name, values, isAdmin))
  if (!sectionFields.length) return null
  const skipped = new Set<string>()

  return (
    <section
      id={sectionDomId(label)}
      className="rounded-lg border border-amber-400/10 overflow-hidden"
      data-manual-section={id}
    >
      <button type="button" onClick={onToggle} className="flex w-full items-center gap-2 bg-black/20 px-3 py-2 text-left">
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <span className="text-[11px] font-medium text-stone-200">{label}</span>
        <span className="ml-auto text-[9px] text-stone-600">
          {sectionFields.filter((f) => (values[f.name] || '').trim()).length}/{sectionFields.length}
        </span>
      </button>
      {open && (
        <div className="grid grid-cols-3 gap-2 p-3">
          {id === 'more'
            ? moreBlocks(sectionFields).map((block) => (
                <div key={block.group} className="col-span-3 space-y-2">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-stone-500">{block.group}</p>
                  <div className="grid grid-cols-3 gap-2">
                    {block.fields.map((f) => renderField(f, block.fields))}
                  </div>
                </div>
              ))
            : sectionFields.map((f) => {
                if (skipped.has(f.name)) return null
                if (id === 'request' && isPairLeft(f.name)) {
                  const mateName = pairMate(f.name)
                  const mate = sectionFields.find((x) => x.name === mateName)
                  if (mate) {
                    skipped.add(mate.name)
                    return (
                      <div key={f.name} className="col-span-3 grid grid-cols-2 gap-2">
                        {renderField(f, sectionFields)}
                        {renderField(mate, sectionFields)}
                      </div>
                    )
                  }
                }
                return renderField(f, sectionFields)
              })}
        </div>
      )}
    </section>
  )
}

function moreBlocks(fields: DealFieldDef[]): Array<{ group: string; fields: DealFieldDef[] }> {
  const order: string[] = []
  const byGroup = new Map<string, DealFieldDef[]>()
  for (const def of fields) {
    if (!byGroup.has(def.group)) {
      byGroup.set(def.group, [])
      order.push(def.group)
    }
    byGroup.get(def.group)!.push(def)
  }
  return order.map((group) => ({ group, fields: byGroup.get(group)! }))
}

function FieldInput({
  def,
  value,
  error,
  className,
  onChange,
  onResolved,
  mapsKey,
  isAdmin = false,
  assetType,
  currency,
  derivedValue,
  derivedNote,
}: {
  def: DealFieldDef
  value: string
  error?: string
  className: string
  onChange: (v: string) => void
  onResolved?: (address: ResolvedAddress) => void
  mapsKey?: string | null
  isAdmin?: boolean
  assetType?: string
  currency?: string
  derivedValue?: number
  derivedNote?: string
}) {
  const options =
    def.name === 'stage'
      ? stageChoices(def.options, isAdmin)
      : def.name === 'state'
        ? intakeStateOptions(def.options)
        : def.options
  const span =
    def.kind === 'text' && (def.name === 'property_name' || def.name === 'street')
      ? 'col-span-2'
      : ''
  const unit = def.kind === 'currency' ? (currency || def.unit) : def.unit
  const assumed = !value ? assumedValue(def.name, assetType) : null
  const hasOwnEmpty = def.name === 'assignee' && options.includes('Unassigned')
  const listId = useId()
  const category = def.kind === 'select' ? null : referenceCategoryFor(def.name)
  const { data: suggestions = [] } = useQuery({
    queryKey: ['reference-values', category],
    queryFn: () => referenceDataService.listValues(category as string),
    enabled: category !== null,
    staleTime: 5 * 60_000,
    retry: false,
  })
  const calculated = CALCULATED_READONLY_FIELDS.has(def.name)
  const calculatedText =
    derivedValue === undefined
      ? ''
      : def.kind === 'percent'
        ? formatPoints(derivedValue)
        : def.kind === 'currency'
          ? formatMoney(derivedValue)
          : String(derivedValue)

  return (
    <label className={`block space-y-1 ${span}`}>
      <span className="text-[10px] text-stone-500">
        {def.label}
        {blocksCreate(def) ? <span className="text-red-400"> *</span> : null}
        {BACKSHOP_HINT_FIELDS.has(def.name) || (def.required && !blocksCreate(def) && !BACKSHOP_HINT_FIELDS.has(def.name)) ? (
          <span className="text-stone-600" title="Needed for Backshop — can be filled now or later">
            {' '}
            · Needed for Backshop
          </span>
        ) : null}
        {unit ? <span className="text-stone-700"> ({unit})</span> : null}
      </span>
      {calculated ? (
        <input
          readOnly
          value={calculatedText}
          className={`${className} opacity-80`}
          placeholder="Calculated"
        />
      ) : def.kind === 'select' ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} className={className}>
          {hasOwnEmpty ? null : <option value="">{emptyOptionLabel(def.name, assetType, options)}</option>}
          {options.map((o) => (
            <option key={o} value={o}>
              {def.name === 'unit_of_measure' ? measureDisplayLabel(o, assetType, options) : o}
            </option>
          ))}
        </select>
      ) : def.name === 'street' ? (
        <AddressAutocomplete
          value={displayValue(value)}
          onChange={onChange}
          onResolved={onResolved}
          apiKey={mapsKey ?? null}
          className={className}
          placeholder={mapsKey ? 'Start typing an address…' : (def.help ?? undefined)}
        />
      ) : (
        <input
          type={def.kind === 'date' ? 'date' : 'text'}
          value={displayValue(value)}
          onChange={(e) =>
            onChange(def.kind === 'currency' ? formatIfCurrency(e.target.value) : e.target.value)
          }
          className={className}
          placeholder={def.help ?? undefined}
          list={suggestions.length > 0 ? listId : undefined}
          autoComplete={suggestions.length > 0 ? 'off' : undefined}
        />
      )}
      {suggestions.length > 0 ? (
        <datalist id={listId}>
          {suggestions.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
      ) : null}
      {def.name === 'stage' && !isAdmin ? (
        <span className="block text-[9px] text-stone-600">Only an admin can create a deal past Triage.</span>
      ) : null}
      {assumed ? (
        <span className="block text-[9px] text-stone-600" data-testid={`assumed-${def.name}`}>
          Left as is, {def.name === 'unit_of_measure' ? measureDisplayLabel(assumed, assetType, options) : assumed} is assumed
          and recorded as a platform default — pick another to override.
        </span>
      ) : null}
      {calculated && derivedNote ? (
        <span className="block text-[9px] text-stone-600">Calculated · {derivedNote}</span>
      ) : null}
      {def.name === 'fully_funded_amount' && !error ? (
        <span className="block text-[9px] text-stone-600">
          The most the lender is asked to commit — the initial amount unless more is to be advanced later.
        </span>
      ) : null}
      {def.name === 'assignee' ? (
        <span className="block text-[9px] text-stone-600">
          An internal underwriter or user. Follows the Underwriter while the deal is Unassigned.
        </span>
      ) : null}
      {def.name === 'secondary_underwriter' ? (
        <span className="block text-[9px] text-stone-600">
          {options.length === 0
            ? 'No underwriter is on the governed list yet — an administrator adds them in Settings › Reference Data.'
            : 'Optional. Picked from the Underwriter list. Not the deal’s owner.'}
        </span>
      ) : null}
      {def.name === 'underwriter' && def.kind === 'select' && options.length === 0 ? (
        <span className="block text-[9px] text-stone-600">
          No underwriter is on the governed list yet — an administrator adds them in Settings › Reference Data.
        </span>
      ) : null}
      {error && <span className="block text-[9px] text-red-400">{error}</span>}
    </label>
  )
}

function formatIfCurrency(raw: string): string {
  if (!raw.trim()) return raw
  return parseMoneyText(raw) === null ? raw : formatCurrencyTyping(raw)
}
